import { useEffect, useMemo, useRef, useState } from 'react';
import type { GeoJSONSource, LngLatBoundsLike, MapMouseEvent } from 'mapbox-gl';
import { UMMap, UMMapControls, type UMMapHandle } from '../../urbanmap/_components';
import { getMapboxToken } from '../../urbanmap/_components/UMMap';
import { NJText } from '@engie-group/fluid-design-system-react';
import { boundsOf } from './domain';
import { buildingsFC, drawFC, networksFC, perimeterLayers, ringFC, stationLayers, stationsFC } from './mapLayers';
import type { Commune, DrawShape, HeatNetwork, LngLat, PlanBuilding } from './types';
import { MapSearchBar } from './MapSearchBar';

/** Station affichée sur la carte : point de mesure + zone d'influence. */
export interface StationMarker { id: string; name: string; position: LngLat; area: LngLat[]; hidePoint?: boolean; serviceAreas?: (GeoJSON.Polygon | GeoJSON.MultiPolygon)[] }

export const FRANCE_OVERVIEW_BOUNDS: LngLatBoundsLike = [[-5.5, 41], [9.8, 51.5]];

interface Props {
  buildings: PlanBuilding[];
  included: Set<string>;
  outlines: LngLat[][];
  outlineData?: GeoJSON.FeatureCollection;
  /** Tous les réseaux présents dans le périmètre, retenus ou non. */
  networks: HeatNetwork[];
  selectedNetworkIds?: string[];
  /** Recentrage ponctuel (recherche de lieu) : `id` change à chaque demande. */
  focus?: { id: string; shape: LngLat[] } | null;
  initialBounds?: LngLatBoundsLike;
  /** Outil de sélection armé : chaque clic ajoute un sommet. */
  drawing: boolean;
  shape: DrawShape;
  points: LngLat[];
  closed: boolean;
  /** Contour terminé par un double-clic. */
  onPolygon: (points: LngLat[]) => void;
  onDrawPoint: (points: LngLat[]) => void;
  editable?: boolean;
  onEditStart: () => void;
  onEdit: (points: LngLat[]) => void;
  onCancelDrawing?: () => void;
  /** Stations météo affichées dès qu'un périmètre est renseigné. */
  stations?: StationMarker[];
  selectedStation?: string | null;
  /** Demande explicite depuis la liste : afficher le point et toute sa zone de référence. */
  stationToReveal?: { position: LngLat; serviceAreas?: (GeoJSON.Polygon | GeoJSON.MultiPolygon)[] } | null;
  /** Clic sur un point ou une zone de station. */
  onStationPick?: (id: string) => void;
  /** Clic sur un réseau existant à retenir dans le projet. */
  onNetworkPick?: (id: string) => void;
  /** Position suivie à l'écran (fiche station ancrée au point). */
  anchor?: LngLat | null;
  /** Rendu de l'ancre : reçoit les coordonnées pixel dans le conteneur de carte. */
  children?: (pos: { x: number; y: number }) => React.ReactNode;
  /** Barre de recherche UrbanMap en haut à gauche. */
  search?: { communes: Commune[]; communeOutlines: Record<string, LngLat[]>; onCommuneSelect?: (code: string) => void };
}

/** Carte de l'assistant : bâtiments UM du référentiel, inclus/exclus, réseaux et sélection d'une zone. */
export function PerimeterMap({ buildings, included, outlines, outlineData, networks, selectedNetworkIds, focus, initialBounds, drawing, shape, points, closed, onPolygon, onDrawPoint, editable = false, onEditStart, onEdit, onCancelDrawing, stations = [], selectedStation = null, stationToReveal = null, onStationPick, onNetworkPick, anchor = null, children, search }: Props) {
  const mapRef = useRef<UMMapHandle>(null);
  const tokenConfigured = !!getMapboxToken();
  const [basemapError, setBasemapError] = useState(false);
  const [anchorPx, setAnchorPx] = useState<{ x: number; y: number } | null>(null);
  const latest = useRef({ drawing, shape, points, closed, onPolygon, onDrawPoint, onEditStart, onEdit, onCancelDrawing, onStationPick, onNetworkPick, selectedStation });
  latest.current = { drawing, shape, points, closed, onPolygon, onDrawPoint, onEditStart, onEdit, onCancelDrawing, onStationPick, onNetworkPick, selectedStation };
  const suppressClickUntil = useRef(0);
  const editingGesture = useRef(false);

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return undefined;
    const failed = (event: { error: Error; sourceId?: string }) => {
      if (event.sourceId === 'composite') {
        setBasemapError(true);
      }
    };
    map.on('error', failed);
    return () => { map.off('error', failed); };
  }, []);

  const layers = useMemo(() => {
    const weather = stationLayers();
    return [...weather.filter((layer) => layer.id !== 'pc-station-point'), ...perimeterLayers(),
      ...weather.filter((layer) => layer.id === 'pc-station-point')];
  }, []);
  const sources = useMemo(() => ({
    buildings: buildingsFC(buildings, (b) => ({ inc: included.has(b.id) ? 1 : 0 })),
    outline: outlineData ?? ringFC(outlines),
    networks: networksFC(networks, selectedNetworkIds ? new Set(selectedNetworkIds) : undefined),
    draw: drawFC(points, closed),
    stations: stationsFC(stations, selectedStation),
  }), [buildings, included, outlines, outlineData, networks, selectedNetworkIds, points, closed, stations, selectedStation]);

  const hasStations = stations.length > 0;
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    const sync = () => {
      for (const { id } of stationLayers()) {
        const visibility = hasStations ? 'visible' : 'none';
        if (map.getLayer(id) && map.getLayoutProperty(id, 'visibility') !== visibility) {
          map.setLayoutProperty(id, 'visibility', visibility);
        }
      }
    };
    sync();
    map.on('styledata', sync);
    return () => { map.off('styledata', sync); };
  }, [hasStations]);

  /** Un changement de commune recadre, pas la sélection d'un réseau qui la dépasse. */
  const lastFramedOutlines = useRef('');
  useEffect(() => {
    const shapes = outlines.flat();
    const key = JSON.stringify(outlines);
    if (shapes.length && key !== lastFramedOutlines.current) {
      lastFramedOutlines.current = key;
      mapRef.current?.fitBounds(boundsOf(shapes));
    }
  }, [outlines, initialBounds]);

  /** Recherche de lieu : on vole vers la commune demandée. */
  const focusId = focus?.id ?? null;
  useEffect(() => {
    if (!focus || focus.shape.length === 0) return;
    mapRef.current?.fitBounds(boundsOf(focus.shape));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId]);

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !stationToReveal) return;
    const coordinates = [
      stationToReveal.position,
      ...(stationToReveal.serviceAreas ?? []).flatMap((geometry) =>
        geometry.type === 'Polygon' ? geometry.coordinates.flat() : geometry.coordinates.flat(2)),
    ];
    const { clientWidth, clientHeight } = map.getContainer();
    const padding = 48;
    const inset = padding - 1;
    if (coordinates.every(([lng, lat]) => {
      const pixel = map.project([lng, lat]);
      return pixel.x >= inset && pixel.x <= clientWidth - inset
        && pixel.y >= inset && pixel.y <= clientHeight - inset;
    })) return;
    const bounds = map.getBounds();
    if (!bounds) return;
    for (const [lng, lat] of coordinates) bounds.extend([lng, lat]);
    map.fitBounds(bounds, {
      padding, maxZoom: map.getZoom(), duration: 600,
    });
  }, [stationToReveal]);

  /** Ignore the second click of a double-click so it doesn't duplicate the final vertex. */
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !drawing) return undefined;
    const preview = (cursor?: LngLat) => (map.getSource('draw') as GeoJSONSource | undefined)
      ?.setData(drawFC(latest.current.points, false, cursor));
    const click = (e: MapMouseEvent) => {
      if (!latest.current.drawing) return;
      if (e.originalEvent.detail > 1) return;
      map.stop();
      const vertices = latest.current.points;
      latest.current.onDrawPoint([...vertices, [e.lngLat.lng, e.lngLat.lat]]);
    };
    const finish = () => {
      suppressClickUntil.current = performance.now() + 250;
      latest.current.onPolygon(latest.current.points);
    };
    const move = (e: MapMouseEvent) => { preview([e.lngLat.lng, e.lngLat.lat]); };
    const leave = () => { preview(); };
    map.on('click', click);
    map.on('dblclick', finish);
    map.on('mousemove', move);
    map.on('mouseout', leave);
    return () => {
      map.off('click', click);
      map.off('dblclick', finish);
      map.off('mousemove', move);
      map.off('mouseout', leave);
      (map.getSource('draw') as GeoJSONSource | undefined)?.setData(drawFC(latest.current.points, latest.current.closed));
    };
  }, [drawing]);

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || drawing || !editable) return undefined;
    const canvas = map.getCanvas();
    let gesture: { id: number; start: [number, number]; vertices: LngLat[]; vertex: number | null; pan: boolean } | null = null;
    const position = (event: PointerEvent): [number, number] => {
      const bounds = canvas.getBoundingClientRect();
      return [event.clientX - bounds.left, event.clientY - bounds.top];
    };
    const release = () => {
      if (!gesture) return;
      const { id, pan } = gesture;
      gesture = null;
      editingGesture.current = false;
      suppressClickUntil.current = performance.now() + 250;
      if (canvas.hasPointerCapture(id)) canvas.releasePointerCapture(id);
      if (pan) map.dragPan.enable();
    };
    const down = (event: PointerEvent) => {
      if (event.button !== 0 || gesture || latest.current.points.length < 3) return;
      const pixel = position(event);
      const layers = ['pc-draw-vertex', 'pc-draw-midpoint', 'pc-draw-fill'].filter((id) => map.getLayer(id));
      const hits = map.queryRenderedFeatures(pixel, { layers });
      const vertexHit = hits.find((hit) => hit.layer?.id === 'pc-draw-vertex');
      const midpointHit = hits.find((hit) => hit.layer?.id === 'pc-draw-midpoint');
      if (!hits.length) return;
      event.preventDefault();
      event.stopPropagation();
      map.stop();
      const vertices = [...latest.current.points];
      let vertex = vertexHit ? Number(vertexHit.properties?.vertex) : null;
      if (!vertexHit && midpointHit) {
        vertex = Number(midpointHit.properties?.midpoint) + 1;
        const coord = map.unproject(pixel);
        vertices.splice(vertex, 0, [coord.lng, coord.lat]);
      }
      gesture = { id: event.pointerId, start: pixel, vertices, vertex, pan: map.dragPan.isEnabled() };
      editingGesture.current = true;
      map.dragPan.disable();
      canvas.setPointerCapture(event.pointerId);
      latest.current.onEditStart();
      if (midpointHit && !vertexHit) latest.current.onEdit(vertices);
    };
    const move = (event: PointerEvent) => {
      if (!gesture || gesture.id !== event.pointerId) return;
      event.preventDefault();
      event.stopPropagation();
      const pixel = position(event);
      const { start, vertices, vertex } = gesture;
      const updated: LngLat[] = vertices.map((point, index) => {
        if (vertex !== null && index !== vertex) return point;
        const origin = map.project(point);
        const coord = map.unproject(vertex === null
          ? [origin.x + pixel[0] - start[0], origin.y + pixel[1] - start[1]]
          : pixel);
        return [coord.lng, coord.lat];
      });
      latest.current.onEdit(updated);
    };
    const up = (event: PointerEvent) => {
      if (!gesture || gesture.id !== event.pointerId) return;
      move(event);
      release();
    };
    const cancel = () => {
      if (!gesture) return;
      release();
      latest.current.onCancelDrawing?.();
    };
    canvas.addEventListener('pointerdown', down, true);
    canvas.addEventListener('pointermove', move, true);
    canvas.addEventListener('pointerup', up, true);
    canvas.addEventListener('pointercancel', cancel);
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape' || event.key === 'Enter') release(); };
    window.addEventListener('keydown', escape);
    return () => {
      canvas.removeEventListener('pointerdown', down, true);
      canvas.removeEventListener('pointermove', move, true);
      canvas.removeEventListener('pointerup', up, true);
      canvas.removeEventListener('pointercancel', cancel);
      window.removeEventListener('keydown', escape);
      release();
    };
  }, [drawing, editable]);

  /** Pendant le dessin, le glissé trace la zone au lieu de déplacer la carte. */
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    if (!drawing) return;
    const panEnabled = map.dragPan.isEnabled();
    const boxEnabled = map.boxZoom.isEnabled();
    const doubleClickEnabled = map.doubleClickZoom.isEnabled();
    map.dragPan.disable();
    map.boxZoom.disable();
    map.doubleClickZoom.disable();
    return () => {
      if (panEnabled) map.dragPan.enable();
      if (boxEnabled) map.boxZoom.enable();
      if (doubleClickEnabled) map.doubleClickZoom.enable();
    };
  }, [drawing]);

  /** Clic sur une station, sinon sur un réseau existant à ajouter/retirer. */
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return undefined;
    const click = (e: MapMouseEvent) => {
      if (editingGesture.current || performance.now() < suppressClickUntil.current) return;
      if (latest.current.drawing) return;
      const stationPick = latest.current.onStationPick;
      if (stationPick) {
        const stationLayers = ['pc-station-point'].filter((l) => map.getLayer(l));
        const stationId = stationLayers.length
          ? map.queryRenderedFeatures(e.point, { layers: stationLayers })[0]?.properties?.id
          : undefined;
        if (typeof stationId === 'string') { stationPick(stationId); return; }
      }
      const networkPick = latest.current.onNetworkPick;
      const networkId = networkPick && map.getLayer('pc-wiz-network') ? map.queryRenderedFeatures([
        [e.point.x - 6, e.point.y - 6],
        [e.point.x + 6, e.point.y + 6],
      ], { layers: ['pc-wiz-network', 'pc-wiz-network-other'] })[0]?.properties?.id : undefined;
      if (typeof networkId === 'string' && networkPick) { networkPick(networkId); return; }
      if (stationPick && map.getLayer('pc-station-zone')) {
        const zoneId = map.queryRenderedFeatures(e.point, { layers: ['pc-station-zone'] })
          .sort((a, b) => Number(b.properties?.sel ?? 0) - Number(a.properties?.sel ?? 0))[0]?.properties?.id;
        if (typeof zoneId === 'string') stationPick(zoneId);
      }
    };
    map.on('click', click);
    return () => { map.off('click', click); };
  }, []);

  /** Survol des stations et réseaux sélectionnables : curseur pointeur. */
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || (!onStationPick && !onNetworkPick)) return undefined;
    /** La carte peut être détruite ou pas encore initialisée : le canvas n'est alors pas disponible. */
    const canvas = () => { try { return map.getCanvas(); } catch { return undefined; } };
    const move = (e: MapMouseEvent) => {
      if (editingGesture.current) { const c = canvas(); if (c) c.style.cursor = 'grabbing'; return; }
      if (editable && !latest.current.drawing) {
        const drawLayers = ['pc-draw-vertex', 'pc-draw-midpoint', 'pc-draw-fill'].filter((id) => map.getLayer(id));
        if (drawLayers.length && map.queryRenderedFeatures(e.point, { layers: drawLayers }).length) {
          const c = canvas();
          if (c) c.style.cursor = 'grab';
          return;
        }
      }
      const ids = [
        ...(onStationPick ? ['pc-station-point', 'pc-station-zone'] : []),
        ...(onNetworkPick ? ['pc-wiz-network', 'pc-wiz-network-other'] : []),
      ].filter((l) => map.getLayer(l));
      const over = !latest.current.drawing && ids.length > 0
        && map.queryRenderedFeatures([
          [e.point.x - 6, e.point.y - 6],
          [e.point.x + 6, e.point.y + 6],
        ], { layers: ids }).length > 0;
      const c = canvas();
      if (c) c.style.cursor = over ? 'pointer' : '';
    };
    map.on('mousemove', move);
    return () => {
      map.off('mousemove', move);
      const c = canvas();
      if (c) c.style.cursor = '';
    };
  }, [onStationPick, onNetworkPick, editable]);

  /** Suivi de l'ancre : la fiche station reste collée à son point pendant les déplacements. */
  const anchorKey = anchor ? `${anchor[0]},${anchor[1]}` : '';
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !anchor) { setAnchorPx(null); return undefined; }
    const sync = () => {
      const p = map.project(anchor);
      setAnchorPx({ x: p.x, y: p.y });
    };
    sync();
    map.on('move', sync);
    map.on('resize', sync);
    return () => { map.off('move', sync); map.off('resize', sync); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorKey]);

  return (
    <div className={drawing ? 'pc-drawing' : undefined} style={{ position: 'relative', width: '100%', height: '100%' }}>
      <style>{'.pc-drawing .mapboxgl-canvas-container, .pc-drawing .mapboxgl-canvas{cursor:crosshair!important}'}</style>
      {tokenConfigured && <UMMap ref={mapRef} sources={sources} layers={layers} initialBounds={initialBounds} />}
      {(!tokenConfigured || basemapError) && (
        <NJText as="span" scale="xs" className="pcv3-perimeter-map-message" role="alert">
          {basemapError ? 'Fond Mapbox indisponible · vérifiez les autorisations du token et l’accès réseau. Aucun fond hors ligne utilisé.' : 'Fond Mapbox indisponible · configurez VITE_MAPBOX_TOKEN. Aucun fond hors ligne utilisé.'}
        </NJText>
      )}
      <UMMapControls basemap="light" availableBasemaps={['light']} onZoomIn={() => mapRef.current?.zoomIn()} onZoomOut={() => mapRef.current?.zoomOut()} />
      {search && <MapSearchBar mapRef={mapRef} communes={search.communes} communeOutlines={search.communeOutlines} onCommuneSelect={search.onCommuneSelect}
        home={outlines.length ? outlines.flat() : points} />}
      {anchorPx && children?.(anchorPx)}
    </div>
  );
}
